/* ============================================================
   ÉKO '77 — site header

   FOUR JOBS, and only the first one is unconditional.

   1. PUBLISH --head-h.
      The bar is position:fixed, so everything that has to sit under
      it needs its height: the search drawer (assets/x-search.css:
      `top: var(--head-h, 64px)`), anchored bands (x-tokens.css:
      `scroll-margin-top: var(--head-h, 84px)`) and the clearance
      spacer below. The reference hardcodes 64px per page and is
      wrong about it — the shop bar is really 68px tall because the
      round tool buttons are 44px. Measuring removes the guess, and a
      ResizeObserver keeps it honest when the viewport, the fonts or
      the merchant's padding settings change.

   2. SWITCH OVERLAY -> SOLID ON SCROLL.
      The reference toggles `is-solid` at `hero.offsetHeight - 90`
      (home, contact, event-catering) or `mast.offsetHeight - 90/80`
      (about, gallery, restaurants), and ships the class server-side on
      pages that have no dark masthead at all (menu, privacy, shop).
      On Shopify the page decides, not the header: any section that
      wants the bar to float over it marks its root with
      `data-eko-head-overlay` (and optionally
      `data-eko-head-overlay-offset="90"`). Nothing marked -> the bar
      is solid from the first paint, which is the correct default for
      every page built so far.

   3. OPEN THE MOBILE PANEL.
      The burger fires a CANCELABLE `eko:panel:open`. The off-canvas
      panel task (1da5938f) should listen, preventDefault() and slide
      its own panel in. With nothing listening the bar falls back to
      revealing the links it already renders, so mobile navigation
      works today instead of waiting for that task.

   4. KEEP THE BASKET COUNT LIVE.
      Same contract sections/x-cartbar.liquid already established:
      the button fires a cancelable `eko:basket:open` for the drawer
      task, and the count re-reads the cart through the SECTION
      RENDERING API so the theme's own pluralised translation is used
      instead of formatting anything in JS. The fetch/XHR hook is
      shared with the cart bar through window.__xCartHooked, so there
      is exactly one hook however many ÉKO components are on the page.
   ============================================================ */

(function () {
  'use strict';

  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  var UPDATED_EVENT = 'eko:cart:updated';
  var DESKTOP = 1081;

  function announce() {
    document.dispatchEvent(new CustomEvent(UPDATED_EVENT, { detail: { source: 'x-site-head-hook' } }));
  }

  /* one hook per document, shared with assets/x-cartbar.js */
  function installCartHooks() {
    if (window.__xCartHooked) return;
    window.__xCartHooked = true;

    if (window.fetch) {
      var nativeFetch = window.fetch;
      window.fetch = function (input) {
        var url = '';
        try {
          url = typeof input === 'string' ? input : input && input.url ? input.url : '';
        } catch (e) {
          url = '';
        }
        var result = nativeFetch.apply(this, arguments);
        if (url && CART_MUTATION.test(url)) {
          result
            .then(function (response) {
              if (response && response.ok) announce();
              return response;
            })
            .catch(function () {});
        }
        return result;
      };
    }

    var open = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (typeof url === 'string' && CART_MUTATION.test(url)) {
        this.addEventListener('load', function () {
          if (this.status >= 200 && this.status < 300) announce();
        });
      }
      return open.apply(this, arguments);
    };
  }

  class XSiteHead extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('[data-x-head-bar]');
      this.nav = this.querySelector('[data-x-head-nav]');
      this.burger = this.querySelector('[data-x-head-burger]');
      this.basket = this.querySelector('[data-x-head-basket]');
      this.count = this.querySelector('[data-x-head-count]');
      this.sectionId = this.dataset.sectionId || '';
      this.mode = this.dataset.mode || 'auto';
      this.spacer = document.getElementById('x-head-spacer-' + this.sectionId);

      if (!this.bar) return;

      this.onScroll = this.onScroll.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onResize = this.onResize.bind(this);
      this.refreshCount = this.refreshCount.bind(this);

      this.measure();
      this.watchSize();
      this.bindOverlay();
      this.bindBurger();
      this.bindBasket();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.onTransitionEnd) this.bar.removeEventListener('transitionend', this.onTransitionEnd);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener(UPDATED_EVENT, this.refreshCount);
      if (this.unsubscribe) this.unsubscribe();
    }

    /* ---------------- 1. --head-h ---------------- */

    measure() {
      var h = Math.round(this.bar.getBoundingClientRect().height);
      if (!h || h === this.lastHeight) return;
      this.lastHeight = h;
      document.documentElement.style.setProperty('--head-h', h + 'px');
    }

    /* The bar changes height by animating PADDING, and the content box never
       moves while it does. A default ResizeObserver watches the content box,
       so overlay -> solid never wakes it; and the measure() that setSolid()
       fires runs on the first frame of a 0.4s transition, when the height is
       still the old one and `h === lastHeight` bails out. Between the two,
       --head-h kept the overlay value (77px) under a 65px bar and every
       consumer of it — the spacer, the search drawer, the shop toolbar,
       scroll-margin-top — inherited a 12px gap.

       So: watch the BORDER box, and re-measure when the padding transition
       actually lands. */
    watchSize() {
      this.onTransitionEnd = this.onTransitionEnd.bind(this);
      this.bar.addEventListener('transitionend', this.onTransitionEnd);

      if (!('ResizeObserver' in window)) {
        window.addEventListener('resize', this.measure.bind(this), { passive: true });
        return;
      }
      this.resizeObserver = new ResizeObserver(this.measure.bind(this));
      this.resizeObserver.observe(this.bar, { box: 'border-box' });
    }

    onTransitionEnd(event) {
      if (event.target !== this.bar) return;
      if (event.propertyName.indexOf('padding') !== 0) return;
      this.measure();
    }

    /* ---------------- 2. overlay -> solid ---------------- */

    bindOverlay() {
      if (this.mode === 'solid') return this.pin();

      this.overlay =
        this.mode === 'overlay'
          ? null
          : document.querySelector('[data-eko-head-overlay]');

      /* "auto" with nothing to float over is a solid bar, and it stays
         solid — no scroll listener, no work on every frame. */
      if (this.mode === 'auto' && !this.overlay) return this.pin();

      this.apply();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
    }

    threshold() {
      var fallback = Math.max(0, window.innerHeight - 90);
      if (!this.overlay) return fallback;
      var offset = parseInt(this.overlay.dataset.ekoHeadOverlayOffset || '90', 10);
      return Math.max(0, this.overlay.offsetHeight - (isNaN(offset) ? 90 : offset));
    }

    /* Pinned solid: there is nothing on this page for the bar to float
       over, so the state is final. The flag matters because onScroll is
       also reachable from a resize, and re-evaluating the threshold there
       would answer "scrollY 0 is not past innerHeight - 90" and quietly
       strip the class — a transparent bar with white type sitting on a
       paper page, from nothing more than rotating a phone. */
    pin() {
      this.pinned = true;
      this.setGlass(false);
      this.setSolid(true);
    }

    onScroll() {
      if (this.pinned) return;
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(
        function () {
          this.ticking = false;
          this.apply();
        }.bind(this)
      );
    }

    /* THREE states, not two. Bare at the very top is the design — the bar
       is meant to sit on the hero with nothing behind it. The trouble is
       the middle: while the first section scrolls past, whatever it is
       made of runs under a bar that still has no background, and a white
       masthead title under white nav type is simply gone (about: the h1
       crosses the bar between 291px and 469px of scroll, and solid only
       arrives at 486). So the bar takes its glass after a flick of scroll
       and keeps it until the section is behind it, where solid takes over.

       48px is "the page has moved", not "the visitor is reading" — far
       enough that the bare bar is what you see on arrival, early enough
       that the glass is always on before anything reaches the bar. */
    apply() {
      var GLASS_AT = 48;
      var y = window.scrollY;
      var solid = y > this.threshold();
      this.setSolid(solid);
      this.setGlass(!solid && y > GLASS_AT);
    }

    setGlass(on) {
      if (on === this.glass) return;
      this.glass = on;
      this.bar.classList.toggle('x-head--glass', on);
    }

    setSolid(on) {
      if (on === this.solid) return;
      this.solid = on;
      this.bar.classList.toggle('x-head--solid', on);
      this.measure();
    }

    /* ---------------- 3. burger ---------------- */

    bindBurger() {
      if (!this.burger) return;

      this.burger.addEventListener(
        'click',
        function () {
          if (this.bar.classList.contains('is-sheet-open')) return this.closeSheet();

          var event = new CustomEvent('eko:panel:open', { bubbles: true, cancelable: true });
          document.dispatchEvent(event);
          /* the off-canvas panel task takes it from here */
          if (event.defaultPrevented) return;

          this.openSheet();
        }.bind(this)
      );

      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);
      /* the sheet must not survive a jump to desktop, where the burger
         that opened it is display:none and could never close it again */
      window.addEventListener('resize', this.onResize, { passive: true });
    }

    onResize() {
      if (window.innerWidth >= DESKTOP) this.closeSheet();
    }

    openSheet() {
      this.bar.classList.add('is-sheet-open');
      this.burger.setAttribute('aria-expanded', 'true');
    }

    closeSheet() {
      if (!this.bar.classList.contains('is-sheet-open')) return;
      this.bar.classList.remove('is-sheet-open');
      this.burger.setAttribute('aria-expanded', 'false');
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.closeSheet();
    }

    onDocumentClick(event) {
      if (!this.bar.classList.contains('is-sheet-open')) return;
      if (event.target.closest('[data-x-head-burger]')) return;
      if (event.target.closest('[data-x-head-nav]')) return this.closeSheet();
      if (!event.target.closest('[data-x-head-bar]')) this.closeSheet();
    }

    /* ---------------- 4. basket ---------------- */

    bindBasket() {
      if (!this.basket) return;

      this.basket.addEventListener(
        'click',
        function () {
          var event = new CustomEvent('eko:basket:open', { bubbles: true, cancelable: true });
          document.dispatchEvent(event);
          if (event.defaultPrevented) return;

          var drawer = document.querySelector('cart-drawer:not(.is-empty)');
          if (drawer && typeof drawer.open === 'function') return drawer.open();

          window.location.href = this.basket.dataset.cartUrl || '/cart';
        }.bind(this)
      );

      installCartHooks();
      document.addEventListener(UPDATED_EVENT, this.refreshCount);

      if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        this.unsubscribe = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, this.refreshCount);
      }
    }

    /* Re-render THIS section through the Section Rendering API and lift the
       count out of the response: the number and its aria-label keep coming
       from Liquid, so translations and pluralisation are never reimplemented
       here. /cart.js is only the fallback when that render fails. */
    refreshCount() {
      if (!this.count || !this.sectionId) return;

      fetch(window.location.pathname + '?sections=' + encodeURIComponent(this.sectionId), {
        headers: { Accept: 'application/json' }
      })
        .then(function (response) {
          if (!response.ok) throw new Error(response.status);
          return response.json();
        })
        .then(
          function (data) {
            var markup = data[this.sectionId];
            if (!markup) throw new Error('empty');
            var fresh = new DOMParser()
              .parseFromString(markup, 'text/html')
              .querySelector('[data-x-head-count]');
            if (!fresh) throw new Error('no count');
            this.paint(parseInt(fresh.textContent, 10) || 0, fresh.getAttribute('data-label'));
          }.bind(this)
        )
        .catch(
          function () {
            fetch('/cart.js', { headers: { Accept: 'application/json' } })
              .then(function (response) {
                return response.json();
              })
              .then(
                function (cart) {
                  this.paint(cart.item_count || 0, null);
                }.bind(this)
              )
              .catch(function () {});
          }.bind(this)
        );
    }

    paint(value, label) {
      var previous = parseInt(this.count.textContent, 10) || 0;
      this.count.textContent = value;
      this.count.classList.toggle('is-on', value > 0);

      if (label && this.basket) this.basket.setAttribute('aria-label', label);

      if (value > previous) {
        this.count.classList.remove('is-bump');
        /* reading offsetWidth restarts the keyframes */
        void this.count.offsetWidth;
        this.count.classList.add('is-bump');
      }
    }
  }

  if (!customElements.get('x-site-head')) customElements.define('x-site-head', XSiteHead);
})();
